import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  Users, 
  FolderOpen, 
  FileText, 
  AlertTriangle,
  TrendingUp,
  Clock,
  Plus,
  ArrowRight,
  Loader2,
  CheckSquare,
  Calendar,
  AlertCircle,
  MessageCircle,
  Eye,
  Target,
  ClipboardList,
  UserCheck
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import TasksWidget from '../components/TasksWidget';
import TasksAssignedWidget from '../components/TasksAssignedWidget';
import AppointmentsWidget from '../components/AppointmentsWidget';
import { formatDate } from '../utils/dateFormat';
import { DashboardWelcomeBanner } from '../components/DashboardWelcomeBanner';
import { NeedsReviewFlagsCard } from '../components/caseload-flags/NeedsReviewFlagsCard';
import { OutreachQueueDashboardCard } from '../components/OutreachQueueDashboardCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Roles that see leadership dashboard (Active Participants, Open Cases - no food pantry)
const LEADERSHIP_ROLES = ['system_admin', 'executive', 'operations_manager'];

// Roles that see SNAP expirations widgets
const SNAP_EXPIRATION_ROLES = ['system_admin', 'executive', 'operations_manager', 'case_manager', 'case_manager_supervisor'];

const DashboardPage = () => {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [caseload, setCaseload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingReviews, setPendingReviews] = useState({ pending_corrections: [], pending_approvals: [] });
  const [snapCertExpirations, setSnapCertExpirations] = useState(null);
  const [snapEnrollmentExpirations, setSnapEnrollmentExpirations] = useState(null);
  const [snapRetentionExpirations, setSnapRetentionExpirations] = useState(null);
  const [seasonalBanner, setSeasonalBanner] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState('all');
  const [programLoading, setProgramLoading] = useState(false);
  
  const isLeadership = LEADERSHIP_ROLES.includes(user?.role);
  const showSnapExpirations = SNAP_EXPIRATION_ROLES.includes(user?.role);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [metricsRes, caseloadRes, reviewsRes, bannerRes] = await Promise.all([
        axios.get(`${API}/dashboard/metrics`),
        axios.get(`${API}/dashboard/my-caseload`),
        axios.get(`${API}/case-notes/pending-reviews`),
        axios.get(`${API}/dashboard-banner/active`).catch(() => ({ data: { banner: null } }))
      ]);
      setSeasonalBanner(bannerRes.data.banner);
      setMetrics(metricsRes.data);
      setCaseload(caseloadRes.data);
      setPendingReviews(reviewsRes.data);
      
      // Fetch SNAP expirations if user has permission
      if (SNAP_EXPIRATION_ROLES.includes(user?.role)) {
        try {
          const [certRes, enrollmentRes, retentionRes] = await Promise.all([
            axios.get(`${API}/reports/snap-et-cert-expirations?days_ahead=90`),
            axios.get(`${API}/reports/snap-et-enrollment-expirations?days_ahead=90`),
            axios.get(`${API}/reports/snap-et-retention-expirations?days_ahead=90`)
          ]);
          setSnapCertExpirations(certRes.data);
          setSnapEnrollmentExpirations(enrollmentRes.data);
          setSnapRetentionExpirations(retentionRes.data);
        } catch (err) {
          console.error('Failed to fetch SNAP expirations:', err);
        }
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const changeProgram = async (value) => {
    setSelectedProgram(value);
    setProgramLoading(true);
    try {
      const res = await axios.get(`${API}/dashboard/metrics`, { params: value === 'all' ? {} : { program_type: value } });
      setMetrics(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load program numbers');
    } finally {
      setProgramLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {isLeadership && (
        <div className="flex items-center justify-end gap-2" data-testid="dashboard-program-filter">
          {programLoading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
          <span className="text-sm text-muted-foreground">Program</span>
          <Select value={selectedProgram} onValueChange={changeProgram}>
            <SelectTrigger className="w-72 bg-card" data-testid="dashboard-program-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="dashboard-program-option-all">All Programs</SelectItem>
              {(metrics?.program_options || []).map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`dashboard-program-option-${p.id}`}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Welcome Section */}
      <DashboardWelcomeBanner firstName={user?.name?.split(' ')[0]} isLeadership={isLeadership} banner={seasonalBanner} />

      <OutreachQueueDashboardCard role={user?.role} />

      {/* Stats Cards - Role-Based */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Leadership sees Active Participants (excludes Food Pantry) */}
        {isLeadership && (
          <Card className="card-shadow" data-testid="stat-participants">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Active Participants
              </CardTitle>
              <Users className="w-5 h-5 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{metrics?.participants?.active || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {metrics?.participants?.total || 0} total enrolled
              </p>
            </CardContent>
          </Card>
        )}

        {/* Leadership sees Open Cases (excludes Food Pantry) */}
        {isLeadership && (
          <Card className="card-shadow" data-testid="stat-open-cases">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Open Cases
              </CardTitle>
              <FolderOpen className="w-5 h-5 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{metrics?.cases?.open || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {metrics?.cases?.total || 0} total cases
              </p>
            </CardContent>
          </Card>
        )}

        {/* Case Managers see My Caseload */}
        {!isLeadership && (
          <Card className="card-shadow" data-testid="stat-caseload">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                My Caseload
              </CardTitle>
              <FileText className="w-5 h-5 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{caseload?.summary?.total || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {caseload?.summary?.high_priority || 0} high priority
              </p>
            </CardContent>
          </Card>
        )}

        {/* Case Managers see Pending Reviews */}
        {!isLeadership && (
          <Card className="card-shadow" data-testid="stat-pending-reviews">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Pending Reviews
              </CardTitle>
              <Clock className="w-5 h-5 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{metrics?.pending_reviews || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Notes awaiting approval
              </p>
            </CardContent>
          </Card>
        )}

        {/* Case Managers see Total Tasks */}
        {!isLeadership && (
          <Card className="card-shadow" data-testid="stat-total-tasks">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Tasks
              </CardTitle>
              <CheckSquare className="w-5 h-5 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{metrics?.total_tasks || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Active tasks assigned
              </p>
            </CardContent>
          </Card>
        )}

        {/* Leadership sees Pending Reviews */}
        {isLeadership && (
          <Card className="card-shadow" data-testid="stat-pending-reviews">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Pending Reviews
              </CardTitle>
              <Clock className="w-5 h-5 text-accent" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{metrics?.pending_reviews || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Notes awaiting approval
              </p>
            </CardContent>
          </Card>
        )}

        {/* Leadership sees Total Tasks */}
        {isLeadership && (
          <Card className="card-shadow" data-testid="stat-total-tasks">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Tasks
              </CardTitle>
              <CheckSquare className="w-5 h-5 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{metrics?.total_tasks || 0}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Active tasks assigned
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* SNAP Expirations Section - Visible to authorized roles */}
      {showSnapExpirations && (snapCertExpirations || snapEnrollmentExpirations) && (
        <div className="space-y-6">
          {/* SNAP Certification Expirations */}
          {snapCertExpirations && snapCertExpirations.expiring_cases?.length > 0 && (
            <Card className="card-shadow border-purple-200 dark:border-purple-800" data-testid="snap-cert-expirations-widget">
              <CardHeader className="flex flex-row items-center justify-between">
                <div className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-purple-500" />
                  <div>
                    <CardTitle className="text-lg">SNAP Certification Expirations</CardTitle>
                    <CardDescription>Certification periods expiring within 90 days</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300">
                    {snapCertExpirations.summary.total_expiring} expiring
                  </Badge>
                  {snapCertExpirations.summary.critical_within_7_days > 0 && (
                    <Badge variant="destructive">
                      {snapCertExpirations.summary.critical_within_7_days} critical
                    </Badge>
                  )}
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/reports" state={{ activeTab: 'snap-cert-expirations' }}>
                      View Full Report
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Urgency</TableHead>
                        <TableHead>Participant</TableHead>
                        <TableHead>Case ID</TableHead>
                        <TableHead>Program</TableHead>
                        <TableHead>Cert. End Date</TableHead>
                        <TableHead>Days Left</TableHead>
                        <TableHead>Assigned To</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {snapCertExpirations.expiring_cases.slice(0, 5).map((item) => (
                        <TableRow key={item.case_id}>
                          <TableCell>
                            <Badge 
                              variant={item.urgency === 'critical' ? 'destructive' : item.urgency === 'warning' ? 'warning' : 'secondary'}
                              className={item.urgency === 'critical' ? 'bg-red-500' : item.urgency === 'warning' ? 'bg-yellow-500' : ''}
                            >
                              {item.urgency === 'critical' ? '⚠️ Critical' : item.urgency === 'warning' ? '⏰ Warning' : 'Normal'}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium">{item.participant_name}</TableCell>
                          <TableCell className="font-mono text-sm">{item.case_id}</TableCell>
                          <TableCell>{item.program_name}</TableCell>
                          <TableCell>{formatDate(item.certification_end_date)}</TableCell>
                          <TableCell>
                            <Badge variant={item.days_until_expiration <= 7 ? 'destructive' : 'outline'}>
                              {item.days_until_expiration} days
                            </Badge>
                          </TableCell>
                          <TableCell>{item.assigned_staff_name || 'Unassigned'}</TableCell>
                          <TableCell>
                            <Link to={`/cases/${item.case_id}`}>
                              <Button variant="outline" size="sm">
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* SNAP Enrollment Expirations */}
          {snapEnrollmentExpirations && snapEnrollmentExpirations.expiring_items?.length > 0 && (
            <Card className="card-shadow border-blue-200 dark:border-blue-800" data-testid="snap-enrollment-expirations-widget">
              <CardHeader className="flex flex-row items-center justify-between">
                <div className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-blue-500" />
                  <div>
                    <CardTitle className="text-lg">SNAP Enrollment Expirations</CardTitle>
                    <CardDescription>Enrollments expiring within 90 days</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                    {snapEnrollmentExpirations.summary.total_expiring} expiring
                  </Badge>
                  {snapEnrollmentExpirations.summary.critical_within_7_days > 0 && (
                    <Badge variant="destructive">
                      {snapEnrollmentExpirations.summary.critical_within_7_days} critical
                    </Badge>
                  )}
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/reports" state={{ activeTab: 'snap-enrollment-expirations' }}>
                      View Full Report
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Urgency</TableHead>
                        <TableHead>Participant</TableHead>
                        <TableHead>Program</TableHead>
                        <TableHead>State</TableHead>
                        <TableHead>End Date</TableHead>
                        <TableHead>Days Left</TableHead>
                        <TableHead>Case Manager</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {snapEnrollmentExpirations.expiring_items.slice(0, 5).map((item) => (
                        <TableRow key={`enrollment-${item.id}`}>
                          <TableCell>
                            <Badge 
                              variant={item.urgency === 'critical' ? 'destructive' : item.urgency === 'warning' ? 'warning' : 'secondary'}
                              className={item.urgency === 'critical' ? 'bg-red-500' : item.urgency === 'warning' ? 'bg-yellow-500' : ''}
                            >
                              {item.urgency === 'critical' ? '⚠️ Critical' : item.urgency === 'warning' ? '⏰ Warning' : 'Normal'}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium">{item.participant_name}</TableCell>
                          <TableCell>{item.program_name}</TableCell>
                          <TableCell>{item.state}</TableCell>
                          <TableCell>{item.end_date}</TableCell>
                          <TableCell>
                            <Badge variant={item.days_until_expiration <= 7 ? 'destructive' : 'outline'}>
                              {item.days_until_expiration} days
                            </Badge>
                          </TableCell>
                          <TableCell>{item.case_manager_name || 'Unassigned'}</TableCell>
                          <TableCell>
                            <Link to={`/participants/${item.participant_id}`}>
                              <Button variant="outline" size="sm">
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* SNAP Retention Expirations Card */}
          {snapRetentionExpirations && snapRetentionExpirations.expiring_items?.length > 0 && (
            <Card className="card-shadow border-purple-200 dark:border-purple-800" data-testid="retention-expirations-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-purple-600" />
                  <div>
                    <CardTitle className="text-lg">SNAP Retention Expirations</CardTitle>
                    <CardDescription>Retention periods approaching or past expiration</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300">
                    {snapRetentionExpirations.summary?.total_expiring || snapRetentionExpirations.expiring_items?.length || 0} expiring
                  </Badge>
                  {(snapRetentionExpirations.summary?.critical_within_7_days > 0 || snapRetentionExpirations.expiring_items?.filter(i => i.days_until_expiration <= 7).length > 0) && (
                    <Badge variant="destructive">
                      {snapRetentionExpirations.summary?.critical_within_7_days || snapRetentionExpirations.expiring_items?.filter(i => i.days_until_expiration <= 7).length} critical
                    </Badge>
                  )}
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/reports" state={{ activeTab: 'retention' }}>
                      View Full Report
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Urgency</TableHead>
                        <TableHead>Participant</TableHead>
                        <TableHead>Program</TableHead>
                        <TableHead>State</TableHead>
                        <TableHead>Retention Start</TableHead>
                        <TableHead>Retention End</TableHead>
                        <TableHead>Days Left</TableHead>
                        <TableHead>Case Manager</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {snapRetentionExpirations.expiring_items.slice(0, 10).map((item, index) => {
                        // Calculate urgency based on days until expiration
                        // Critical = 7 days or fewer, Warning = 8-30 days, Normal = >30 days
                        const daysLeft = item.days_until_expiration;
                        let urgencyLabel = 'Normal';
                        let urgencyClass = '';
                        if (daysLeft <= 7) {
                          urgencyLabel = '⚠️ Critical';
                          urgencyClass = 'bg-red-500';
                        } else if (daysLeft <= 30) {
                          urgencyLabel = '⏰ Warning';
                          urgencyClass = 'bg-yellow-500';
                        }
                        
                        return (
                          <TableRow key={item.id || index}>
                            <TableCell>
                              <Badge 
                                variant={daysLeft <= 7 ? 'destructive' : daysLeft <= 30 ? 'warning' : 'secondary'}
                                className={urgencyClass}
                              >
                                {urgencyLabel}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium">
                              <Link to={`/participants/${item.participant_id}`} className="text-purple-600 hover:underline">
                                {item.participant_name}
                              </Link>
                            </TableCell>
                            <TableCell>{item.program_name}</TableCell>
                            <TableCell>{item.state || 'N/A'}</TableCell>
                            <TableCell>{item.retention_start_date || 'N/A'}</TableCell>
                            <TableCell>{item.retention_end_date}</TableCell>
                            <TableCell>
                              <Badge variant={daysLeft <= 7 ? 'destructive' : 'outline'}>
                                {daysLeft < 0 
                                  ? `${Math.abs(daysLeft)} days ago` 
                                  : `${daysLeft} days`}
                              </Badge>
                            </TableCell>
                            <TableCell>{item.case_manager_name || 'Unassigned'}</TableCell>
                            <TableCell>
                              <Link to={`/participants/${item.participant_id}`}>
                                <Button variant="outline" size="sm">
                                  <Eye className="w-4 h-4 mr-1" />
                                  View
                                </Button>
                              </Link>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                {snapRetentionExpirations.expiring_items?.length > 10 && (
                  <p className="text-sm text-muted-foreground text-center mt-2">
                    Showing 10 of {snapRetentionExpirations.expiring_items.length} retention expirations
                  </p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* My Tasks Widget */}
      <TasksWidget />

      {/* Tasks Assigned Widget - Only visible to sys_admin, executive, operations_manager */}
      <TasksAssignedWidget />

      {/* Caseload Reviews flagged for correction */}
      <NeedsReviewFlagsCard />

      {/* Pending Case Note Reviews Widget */}
      {(pendingReviews.pending_corrections?.length > 0 || pendingReviews.pending_approvals?.length > 0) && (
        <Card className="card-shadow border-orange-200 dark:border-orange-800" data-testid="pending-reviews-widget">
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-500" />
              <div>
                <CardTitle className="text-lg">Pending Case Note Reviews</CardTitle>
                <CardDescription>Notes requiring your attention</CardDescription>
              </div>
            </div>
            <Badge className="bg-orange-100 text-orange-800">
              {(pendingReviews.pending_corrections?.length || 0) + (pendingReviews.pending_approvals?.length || 0)} pending
            </Badge>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Notes You Need to Correct */}
            {pendingReviews.pending_corrections?.length > 0 && (
              <div>
                <h4 className="font-medium text-sm text-orange-700 dark:text-orange-400 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Notes You Need to Correct ({pendingReviews.pending_corrections.length})
                </h4>
                <div className="space-y-2">
                  {pendingReviews.pending_corrections.slice(0, 5).map((note) => (
                    <div key={note.id} className="p-3 rounded-lg border border-orange-200 bg-orange-50/50 dark:bg-orange-900/10 dark:border-orange-800">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{note.subject || 'Untitled Note'}</p>
                          <p className="text-xs text-muted-foreground">
                            Review requested by {note.current_review?.reviewer_name}
                          </p>
                          <p className="text-xs text-orange-700 dark:text-orange-400 mt-1 line-clamp-1">
                            &ldquo;{note.current_review?.comment}&rdquo;
                          </p>
                        </div>
                        <Button size="sm" variant="outline" className="shrink-0 border-orange-400 text-orange-700 hover:bg-orange-100" asChild>
                          <Link to={`/cases/${note.case_id}`} state={{ activeTab: 'notes' }}>
                            Fix Now
                          </Link>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes Awaiting Your Approval (for reviewers) */}
            {pendingReviews.pending_approvals?.length > 0 && (
              <div>
                <h4 className="font-medium text-sm text-blue-700 dark:text-blue-400 mb-2 flex items-center gap-2">
                  <MessageCircle className="w-4 h-4" />
                  Corrections Awaiting Your Approval ({pendingReviews.pending_approvals.length})
                </h4>
                <div className="space-y-2">
                  {pendingReviews.pending_approvals.slice(0, 5).map((note) => (
                    <div key={note.id} className="p-3 rounded-lg border border-blue-200 bg-blue-50/50 dark:bg-blue-900/10 dark:border-blue-800">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{note.subject || 'Untitled Note'}</p>
                          <p className="text-xs text-muted-foreground">
                            Corrected by {note.author_name}
                          </p>
                          {note.current_review?.correction_notes && (
                            <p className="text-xs text-blue-700 dark:text-blue-400 mt-1 line-clamp-1">
                              &ldquo;{note.current_review.correction_notes}&rdquo;
                            </p>
                          )}
                        </div>
                        <Button size="sm" className="shrink-0 bg-blue-600 hover:bg-blue-700" asChild>
                          <Link to={`/cases/${note.case_id}`} state={{ activeTab: 'notes' }}>
                            Review
                          </Link>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tasks & Appointments Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Appointments Widget */}
        <AppointmentsWidget limit={5} showHeader={true} />
        
        {/* Quick Stats - Optional additional card */}
        <Card className="card-shadow">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary" />
              Schedule Overview
            </CardTitle>
            <CardDescription>Your calendar at a glance</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-2xl font-bold text-primary">
                  {metrics?.today_appointments || 0}
                </div>
                <p className="text-xs text-muted-foreground">Today</p>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-2xl font-bold text-primary">
                  {metrics?.week_appointments || 0}
                </div>
                <p className="text-xs text-muted-foreground">This Week</p>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-2xl font-bold text-primary">
                  {metrics?.pending_reviews || 0}
                </div>
                <p className="text-xs text-muted-foreground">Pending Reviews</p>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-2xl font-bold text-primary">
                  {metrics?.total_tasks || 0}
                </div>
                <p className="text-xs text-muted-foreground">Active Tasks</p>
              </div>
            </div>
            <Button variant="outline" className="w-full mt-4" asChild>
              <Link to="/appointments">
                Open Calendar
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* My Cases Table */}
      <Card className="card-shadow" data-testid="my-caseload-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">My Active Cases</CardTitle>
            <CardDescription>Cases assigned to you requiring attention</CardDescription>
          </div>
          <Button variant="outline" size="sm" asChild>
            <Link to="/cases" data-testid="view-all-cases-btn">
              View All
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {caseload?.cases?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Case ID</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Participant</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Service</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Program</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">State</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Priority</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {caseload.cases.slice(0, 5).map((caseItem) => (
                    <tr key={caseItem.id} className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors">
                      <td className="py-3 px-4 text-sm font-mono text-primary">{caseItem.id}</td>
                      <td className="py-3 px-4 text-sm">{caseItem.participant_name}</td>
                      <td className="py-3 px-4 text-sm capitalize">
                        {caseItem.service_type_name || caseItem.service_type?.replace(/_/g, ' ') || caseItem.case_type?.replace(/_/g, ' ')}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {caseItem.program_name || caseItem.program_type?.replace(/_/g, ' ')}
                      </td>
                      <td className="py-3 px-4 text-sm font-medium">{caseItem.state || '-'}</td>
                      <td className="py-3 px-4">
                        <Badge className={
                          caseItem.priority === 'high' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' :
                          caseItem.priority === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' :
                          'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                        }>
                          {caseItem.priority}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button variant="ghost" size="sm" asChild>
                          <Link to={`/cases/${caseItem.id}`} data-testid={`view-case-${caseItem.id}`}>
                            View
                          </Link>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <FolderOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No active cases assigned</p>
              <p className="text-sm mt-2">
                To create a case, go to a <Link to="/participants" className="text-primary hover:underline">participant&apos;s profile</Link>
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Button variant="outline" className="h-auto py-4 justify-start" asChild data-testid="quick-action-participant">
          <Link to="/participants/new">
            <Users className="w-5 h-5 mr-3 text-primary" />
            <div className="text-left">
              <p className="font-medium">Add Participant</p>
              <p className="text-xs text-muted-foreground">Register new client</p>
            </div>
          </Link>
        </Button>
        <Button variant="outline" className="h-auto py-4 justify-start" asChild data-testid="quick-action-case">
          <Link to="/participants">
            <FolderOpen className="w-5 h-5 mr-3 text-primary" />
            <div className="text-left">
              <p className="font-medium">Open Case</p>
              <p className="text-xs text-muted-foreground">Via participant enrollment</p>
            </div>
          </Link>
        </Button>
        <Button variant="outline" className="h-auto py-4 justify-start" asChild data-testid="quick-action-note">
          <Link to="/cases">
            <FileText className="w-5 h-5 mr-3 text-primary" />
            <div className="text-left">
              <p className="font-medium">Write Note</p>
              <p className="text-xs text-muted-foreground">Open case to add note</p>
            </div>
          </Link>
        </Button>
        <Button variant="outline" className="h-auto py-4 justify-start" asChild data-testid="quick-action-search">
          <Link to="/cases">
            <TrendingUp className="w-5 h-5 mr-3 text-primary" />
            <div className="text-left">
              <p className="font-medium">My Caseload</p>
              <p className="text-xs text-muted-foreground">View all assigned cases</p>
            </div>
          </Link>
        </Button>
      </div>
    </div>
  );
};

export default DashboardPage;
